import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Sun, Lightbulb, Power, TrendingDown, Zap, Settings, X, Search, Check, RefreshCw, Edit3, Save, RotateCcw, CheckSquare, Square } from 'lucide-react';
import { ultronTuringApi } from '../../services/ultronTuringApi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  ReferenceLine,
} from 'recharts';

interface DeviceZone {
  id: string;
  name: string;
  devices: string[];
}

interface Device {
  deviceId: string;
  baseSN: string;
  deviceType: string;
  displayName?: string;
  name?: string;
  isOn?: boolean;
  brightness?: number;
  colorTemp?: number;
  traits?: string[];
  attributes?: any;
  deviceSN?: string;
  sn?: string;
  nickname?: string;
  deviceName?: string;
  watt?: number;
  real_power?: number;
}

// Power meter info for energy optimization
interface PowerMeterInfo {
  id: string;
  name: string;
  power?: number;
}

// Energy optimization configuration
interface EnergyOptimizationConfig {
  selectedMeterIds: string[];
  baselinePower: number;  // Legacy single value (fallback)
  baselineSchedule?: number[];  // 24 hourly baseline values (00-23)
}

// Default baseline schedule - typical office lighting pattern (in Watts)
const DEFAULT_BASELINE_SCHEDULE: number[] = Array.from({ length: 24 }, (_, hour) => {
  if (hour >= 0 && hour <= 5) return 200;       // Night: minimal
  if (hour >= 6 && hour <= 7) return 800;       // Early morning
  if (hour >= 8 && hour <= 9) return 2000;      // Morning ramp
  if (hour >= 10 && hour <= 17) return 3000;    // Work hours: peak
  if (hour >= 18 && hour <= 19) return 2500;    // Late afternoon
  if (hour >= 20 && hour <= 21) return 1500;    // Evening
  return 500;                                    // Night prep (22-23)
});

interface AdaptiveLightingViewProps {
  devices: Device[];
  zones: DeviceZone[];
  // Energy Optimization props
  availablePowerMeters?: PowerMeterInfo[];
  energyOptimizationConfig?: EnergyOptimizationConfig;
  onEnergyOptimizationConfigChange?: (config: EnergyOptimizationConfig) => void;
  onOpenZoneSelector?: () => void;
  selectedZoneCount?: number;
  allDevices?: Device[];
}

// Circadian schedule point type
interface SchedulePoint {
  hour: number;
  colorTemp: number;
  brightness: number;
}

// Default circadian schedule data points - 24 hours (00-23)
const DEFAULT_CIRCADIAN_SCHEDULE: SchedulePoint[] = Array.from({ length: 24 }, (_, hour) => {
  // Generate reasonable defaults based on time of day
  let brightness: number;
  let colorTemp: number;
  
  if (hour >= 0 && hour <= 5) {
    // Night (00:00 - 05:00): Low brightness, warm
    brightness = 10;
    colorTemp = 2700;
  } else if (hour >= 6 && hour <= 7) {
    // Early morning (06:00 - 07:00): Gradual wake
    brightness = 30 + (hour - 6) * 20;
    colorTemp = 3000 + (hour - 6) * 500;
  } else if (hour >= 8 && hour <= 9) {
    // Morning (08:00 - 09:00): Ramp up
    brightness = 60 + (hour - 8) * 20;
    colorTemp = 4000 + (hour - 8) * 750;
  } else if (hour >= 10 && hour <= 16) {
    // Work hours (10:00 - 16:00): Full brightness, cool white
    brightness = 100;
    colorTemp = 5500 + Math.min((hour - 10), 2) * 500; // Peak at 6500K around noon
  } else if (hour >= 17 && hour <= 18) {
    // Late afternoon (17:00 - 18:00): Start dimming
    brightness = 90 - (hour - 17) * 10;
    colorTemp = 5000 - (hour - 17) * 500;
  } else if (hour >= 19 && hour <= 20) {
    // Evening (19:00 - 20:00): Warm down
    brightness = 70 - (hour - 19) * 15;
    colorTemp = 4000 - (hour - 19) * 500;
  } else {
    // Night prep (21:00 - 23:00): Low and warm
    brightness = Math.max(10, 40 - (hour - 21) * 15);
    colorTemp = Math.max(2700, 3000 - (hour - 21) * 100);
  }
  
  return { hour, colorTemp: Math.round(colorTemp), brightness: Math.round(brightness) };
});

// Get circadian values based on current time with linear interpolation
const getCircadianValues = (currentHour: number, currentMinute: number = 0, schedule: SchedulePoint[] = DEFAULT_CIRCADIAN_SCHEDULE): { brightness: number; colorTemp: number } => {
  const timeInHours = currentHour + currentMinute / 60;
  
  // Before first schedule point or after last - use edge values
  if (timeInHours <= schedule[0].hour) {
    return { brightness: schedule[0].brightness, colorTemp: schedule[0].colorTemp };
  }
  if (timeInHours >= schedule[schedule.length - 1].hour) {
    return { 
      brightness: schedule[schedule.length - 1].brightness, 
      colorTemp: schedule[schedule.length - 1].colorTemp 
    };
  }
  
  // Find bracketing points and interpolate
  for (let i = 0; i < schedule.length - 1; i++) {
    const current = schedule[i];
    const next = schedule[i + 1];
    
    if (timeInHours >= current.hour && timeInHours < next.hour) {
      const ratio = (timeInHours - current.hour) / (next.hour - current.hour);
      return {
        brightness: Math.round(current.brightness + ratio * (next.brightness - current.brightness)),
        colorTemp: Math.round(current.colorTemp + ratio * (next.colorTemp - current.colorTemp)),
      };
    }
  }
  
  // Fallback
  return { brightness: 50, colorTemp: 4500 };
};

// Generate circadian schedule data for chart display
const generateCircadianData = (schedule: SchedulePoint[]) => {
  return schedule.map(point => ({
    time: `${point.hour.toString().padStart(2, '0')}:00`,
    colorTemp: point.colorTemp,
    brightness: point.brightness,
    hour: point.hour,
  }));
};

// Generate energy optimization data for 24 hours (00-23)
// Only shows Optimized data up to current hour; future hours only have Baseline
const generateEnergyData = (
  baselineSchedule: number[] = DEFAULT_BASELINE_SCHEDULE, 
  currentPower: number = 0,
  historicalPowerData?: (number | null)[] // Optional: actual hourly power data from API (null = no data)
) => {
  const data = [];
  const currentHour = new Date().getHours();
  
  // Generate 24 hours: 00:00 to 23:00
  const hours = Array.from({ length: 24 }, (_, i) => 
    `${i.toString().padStart(2, '0')}:00`
  );
  
  for (let i = 0; i < hours.length; i++) {
    // Baseline from schedule (hourly value)
    const baseline = baselineSchedule[i] || 0;
    
    // Only show Optimized for hours that have data
    let optimized: number | null = null;
    let savingPct: number | null = null;
    
    if (i <= currentHour) {
      // Use historical data if available and not null
      if (historicalPowerData && historicalPowerData[i] !== null && historicalPowerData[i] !== undefined) {
        optimized = historicalPowerData[i];
      } else if (i === currentHour && currentPower > 0) {
        // Fallback: show current power only for current hour when history is missing
        optimized = currentPower;
      }
      // Calculate saving percentage only if we have optimized data
      if (optimized !== null) {
        savingPct = baseline > 0 ? Math.round(((baseline - optimized) / baseline) * 100) : 0;
      }
    }
    
    data.push({
      time: hours[i],
      baseline: Math.round(baseline),
      optimized: optimized !== null ? Math.round(optimized) : null,
      savingPct: savingPct,
      isFuture: i > currentHour,
      hasData: optimized !== null,
    });
  }
  return data;
};

// Initial demo zone data
const initialDemoZones = [
  { id: 'demo-1', name: 'Floor 1 - Lobby', brightness: 0, colorTemp: 4500, isOn: false, autoEnabled: false },
  { id: 'demo-2', name: 'Floor 1 - Conference A', brightness: 100, colorTemp: 5500, isOn: true, autoEnabled: false },
  { id: 'demo-3', name: 'Floor 2 - Open Office', brightness: 75, colorTemp: 4500, isOn: true, autoEnabled: true },
  { id: 'demo-4', name: 'Floor 2 - Break Room', brightness: 60, colorTemp: 3500, isOn: true, autoEnabled: true },
  { id: 'demo-5', name: 'Floor 3 - Executive', brightness: 0, colorTemp: 4000, isOn: false, autoEnabled: false },
  { id: 'demo-6', name: 'Floor 3 - Server Room', brightness: 100, colorTemp: 6500, isOn: true, autoEnabled: false },
];

// Individual Light Control Component with local state
const LightControlItem: React.FC<{
  light: Device;
  deviceSN: string;
}> = ({ light, deviceSN }) => {
  const [localIsOn, setLocalIsOn] = useState(light.isOn ?? false);
  const [localBrightness, setLocalBrightness] = useState(light.brightness ?? 50);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  
  const nickname = light.nickname || light.deviceName;
  
  const handleToggle = async () => {
    console.log('[LightControl] Toggle clicked, current:', localIsOn, 'deviceSN:', deviceSN);
    const newState = !localIsOn;
    setLocalIsOn(newState);
    setIsLoading(true);
    try {
      const command = newState ? 'on' : 'off';
      console.log('[LightControl] Sending command:', command, 'to:', deviceSN);
      await ultronTuringApi.executeCommand(command, [deviceSN], {});
      console.log('[LightControl] Command sent successfully');
    } catch (error) {
      console.error('[LightControl] Failed to toggle light:', error);
      setLocalIsOn(!newState); // Revert on error
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBrightnessChange = (newBrightness: number) => {
    setLocalBrightness(newBrightness);
    
    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      try {
        await ultronTuringApi.executeCommand('setBrightness', [deviceSN], { brightness: newBrightness });
      } catch (error) {
        console.error('Failed to set brightness:', error);
      }
    }, 300);
  };
  
  return (
    <div className="p-3 bg-[#0C2340] rounded-lg space-y-3">
      {/* Device Info Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full transition-colors ${localIsOn ? 'bg-cyan-400' : 'bg-slate-500'}`} />
          <p className="text-sm font-medium text-cyan-400">{nickname || deviceSN}</p>
        </div>
        <button
          onClick={handleToggle}
          disabled={isLoading}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
            localIsOn 
              ? 'bg-cyan-900/30 text-cyan-400 hover:bg-cyan-900/50' 
              : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700/70'
          } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
        >
          {localIsOn ? 'ON' : 'OFF'}
        </button>
      </div>
      
      {/* Brightness Control */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-400">亮度</span>
          <span className="text-cyan-400 font-medium">{localBrightness}%</span>
        </div>
        <div className="relative w-full h-6 flex items-center">
          <div className="absolute left-0 right-0 h-2 rounded-full bg-slate-700" />
          <div 
            className="absolute left-0 h-2 rounded-full bg-gradient-to-r from-cyan-600 to-cyan-400"
            style={{ width: `${localBrightness}%` }}
          />
          <input
            type="range"
            min="0"
            max="100"
            value={localBrightness}
            onChange={(e) => handleBrightnessChange(parseInt(e.target.value))}
            onInput={(e) => handleBrightnessChange(parseInt((e.target as HTMLInputElement).value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            style={{ zIndex: 20, WebkitAppearance: 'none' }}
          />
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-lg pointer-events-none border border-slate-300"
            style={{ left: `calc(${localBrightness}% - 10px)` }}
          />
        </div>
      </div>
    </div>
  );
};

// Zone Light Viewer Modal Component
const ZoneLightViewerModal: React.FC<{
  zoneId: string;
  zoneName: string;
  lights: Device[];
  onClose: () => void;
}> = ({ zoneId, zoneName, lights, onClose }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localLights, setLocalLights] = useState<Device[]>(lights);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Update local lights when props change
  useEffect(() => {
    setLocalLights(lights);
  }, [lights]);
  
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      // Get device SNs for this zone
      const deviceSNs = lights.map(l => l.deviceSN || l.deviceId || l.baseSN).filter(Boolean) as string[];
      if (deviceSNs.length === 0) return;
      
      // Query latest status
      const statusResult = await ultronTuringApi.queryStatus(deviceSNs, { detailed: true });
      
      // Merge status with existing light data
      if (statusResult && typeof statusResult === 'object') {
        const updatedLights = lights.map(light => {
          const sn = light.deviceSN || light.deviceId || light.baseSN;
          const status = statusResult[sn as string];
          if (status) {
            return {
              ...light,
              isOn: status.on ?? status.isOn ?? light.isOn,
              brightness: status.brightness ?? light.brightness,
              colorTemp: status.colorTemperature ?? status.colorTemp ?? light.colorTemp,
            };
          }
          return light;
        });
        setLocalLights(updatedLights);
        setRefreshKey(prev => prev + 1); // Force re-render of LightControlItems
      }
    } catch (error) {
      console.error('Failed to refresh light status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-[#0C2340] border border-slate-700/30 rounded-xl w-full max-w-md mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
          <div>
            <h3 className="text-lg font-semibold text-white">{zoneName} 燈具列表</h3>
            <p className="text-sm text-slate-500">
              共 {localLights.length} 個燈具
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-1.5 rounded-lg transition-colors ${
                isRefreshing 
                  ? 'text-cyan-400 cursor-wait' 
                  : 'text-slate-400 hover:text-cyan-400 hover:bg-slate-700/50'
              }`}
              title="重新整理燈具狀態"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={onClose}
              className="p-1 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Light List */}
        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-3">
          {localLights.map((light) => {
            const deviceSN = light.deviceSN || light.deviceId || light.baseSN;
            return (
              <LightControlItem 
                key={`${deviceSN}-${refreshKey}`} 
                light={light} 
                deviceSN={deviceSN}
              />
            );
          })}
          
          {lights.length === 0 && (
            <div className="text-center py-8">
              <Lightbulb className="w-10 h-10 mx-auto mb-2 text-slate-600" />
              <p className="text-slate-500">此 Zone 沒有燈具</p>
              <p className="text-xs text-slate-600 mt-1">請在 Zone Management 中加入設備</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-700/30">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-700/70 transition-colors font-medium"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
};

const AdaptiveLightingView: React.FC<AdaptiveLightingViewProps> = ({ 
  devices, 
  zones,
  availablePowerMeters = [],
  energyOptimizationConfig,
  onEnergyOptimizationConfigChange,
  onOpenZoneSelector,
  selectedZoneCount,
  allDevices,
}) => {
  const [masterBrightness, setMasterBrightness] = useState(75);
  
  // Energy Optimization Modal state
  const [showEnergyOptModal, setShowEnergyOptModal] = useState(false);
  const [tempSelectedMeters, setTempSelectedMeters] = useState<string[]>([]);
  const [tempBaselineSchedule, setTempBaselineSchedule] = useState<number[]>(DEFAULT_BASELINE_SCHEDULE);
  const scrollableListRef = useRef<HTMLDivElement>(null);
  
  // Auto-focus the scrollable list when modal opens
  useEffect(() => {
    if (showEnergyOptModal && scrollableListRef.current) {
      scrollableListRef.current.focus();
    }
  }, [showEnergyOptModal]);
  
  const [masterColorTemp, setMasterColorTemp] = useState(4500);
  const [circadianEnabled, setCircadianEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('lighting-circadian-enabled');
      return saved === 'true'; // Default to false if not found
    } catch {
      return false;
    }
  });
  
  // Circadian schedule state (persisted to localStorage)
  // Must have exactly 24 points (00-23 hours)
  const [circadianSchedule, setCircadianSchedule] = useState<SchedulePoint[]>(() => {
    try {
      const saved = localStorage.getItem('lighting-circadian-schedule');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate: must be array with exactly 24 points
        if (Array.isArray(parsed) && parsed.length === 24) {
          return parsed;
        }
        // If old format (not 24 points), clear and use default
        console.log('[Circadian] Old schedule format detected, resetting to 24-hour default');
        localStorage.removeItem('lighting-circadian-schedule');
      }
    } catch {}
    return DEFAULT_CIRCADIAN_SCHEDULE;
  });
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<SchedulePoint[]>([]);
  const [zoneStates, setZoneStates] = useState<Record<string, boolean>>(() => {
    // Try to load from localStorage first
    try {
      const saved = localStorage.getItem('lighting-zone-states');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    // Fallback to demo data
    const states: Record<string, boolean> = {};
    initialDemoZones.forEach(z => { states[z.id] = z.isOn; });
    return states;
  });
  const [zoneBrightness, setZoneBrightness] = useState<Record<string, number>>({});
  const [zoneColorTemp, setZoneColorTemp] = useState<Record<string, number>>({});
  const [zoneAutoEnabled, setZoneAutoEnabled] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('lighting-zone-auto-enabled');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    return {};
  });
  const [viewingZoneId, setViewingZoneId] = useState<string | null>(null);
  
  // MCP Command state
  const [isCommandLoading, setIsCommandLoading] = useState(false);
  const commandDebounce = useRef<NodeJS.Timeout | null>(null);
  
  // Light Selector state
  const [showLightSelector, setShowLightSelector] = useState(false);
  const [selectedLights, setSelectedLights] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('lighting-selected-lights');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [lightSearch, setLightSearch] = useState('');
  
  // Power Meter Selector state
  const [selectedPowerMeter, setSelectedPowerMeter] = useState<string>(() => {
    try {
      return localStorage.getItem('lighting-selected-power-meter') || '';
    } catch {
      return '';
    }
  });
  const [showPowerMeterSelector, setShowPowerMeterSelector] = useState(false);
  const [powerMeterSearch, setPowerMeterSearch] = useState('');
  
  // Get all meter devices
  const powerMeters = useMemo(() => {
    return devices.filter(d => 
      d.deviceType === 'power_meter' || 
      d.deviceType?.toLowerCase().includes('meter') ||
      d.traits?.some(t => t.toLowerCase().includes('meter')) ||
      d.watt !== undefined ||
      d.real_power !== undefined
    );
  }, [devices]);
  
  // Filter power meters based on search
  const filteredPowerMeters = useMemo(() => {
    if (!powerMeterSearch) return powerMeters;
    const search = powerMeterSearch.toLowerCase();
    return powerMeters.filter(pm => {
      const sn = (pm.deviceSN || pm.sn || pm.deviceId || pm.baseSN || '').toLowerCase();
      const nickname = (pm.nickname || '').toLowerCase();
      const deviceName = (pm.deviceName || pm.displayName || pm.name || '').toLowerCase();
      return sn.includes(search) || nickname.includes(search) || deviceName.includes(search);
    });
  }, [powerMeters, powerMeterSearch]);
  
  // Helper to get device SN
  const getDeviceSN = (d: Device) => d.deviceSN || d.sn || d.baseSN || d.deviceId;
  
  // Handle power meter selection
  const handleSelectPowerMeter = (sn: string) => {
    setSelectedPowerMeter(sn);
    try {
      localStorage.setItem('lighting-selected-power-meter', sn);
    } catch {}
    setShowPowerMeterSelector(false);
    setPowerMeterSearch('');
  };
  
  const circadianData = useMemo(() => generateCircadianData(circadianSchedule), [circadianSchedule]);
  
  // Calculate current power from selected meters
  const selectedMetersPower = useMemo(() => {
    if (!energyOptimizationConfig?.selectedMeterIds?.length) return 0;
    return availablePowerMeters
      .filter(m => energyOptimizationConfig.selectedMeterIds.includes(m.id))
      .reduce((sum, m) => sum + (m.power || 0), 0);
  }, [availablePowerMeters, energyOptimizationConfig?.selectedMeterIds]);
  
  // Current time for chart indicator (updates every minute)
  const [currentTimeStr, setCurrentTimeStr] = useState(() => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:00`;
  });
  
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTimeStr(`${now.getHours().toString().padStart(2, '0')}:00`);
    };
    const interval = setInterval(updateTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);
  
  // Historical power data for energy optimization chart (real data from API)
  // null means no data for that hour (different from 0 which means actual 0 power)
  const [historicalPowerData, setHistoricalPowerData] = useState<(number | null)[]>([]);
  const [isLoadingHistoricalData, setIsLoadingHistoricalData] = useState(false);
  
  const selectedMeterRecords = useMemo(() => {
    const ids = energyOptimizationConfig?.selectedMeterIds || [];
    if (ids.length === 0) return [];
    const sourceDevices = allDevices || devices;
    return ids.map(id => {
      const device = sourceDevices.find(d => d.deviceId === id || d.baseSN === id);
      const deviceSN = device?.deviceSN || device?.sn || null;
      return {
        id,
        deviceSN,
        baseSN: device?.baseSN,
      };
    });
  }, [energyOptimizationConfig?.selectedMeterIds, devices, allDevices]);

  // Fetch real historical power data for selected meters
  useEffect(() => {
    const fetchHistoricalData = async () => {
      if (selectedMeterRecords.length === 0) {
        setHistoricalPowerData([]);
        return;
      }
      
      setIsLoadingHistoricalData(true);
      try {
        const now = new Date();
        const currentHour = now.getHours();
        
        // Build end time as current hour
        const endTime = new Date();
        endTime.setMinutes(0, 0, 0);
        const endTimeStr = endTime.toISOString();
        
        // Duration from 00:00 to now
        const duration = `${currentHour + 1}h`;
        
        console.log('[EnergyOpt] Fetching historical data for meters:', selectedMeterRecords.map(r => ({
          id: r.id,
          deviceSN: r.deviceSN,
          baseSN: r.baseSN
        })));
        console.log('[EnergyOpt] Duration:', duration, 'EndTime:', endTimeStr);
        
        // Call generateReport for each meter and aggregate
        // Use null for hours without data (instead of 0)
        const hourlyData: (number | null)[] = new Array(24).fill(null);
        
        for (const meter of selectedMeterRecords) {
          const meterKey = meter.deviceSN || meter.baseSN || meter.id;
          try {
            const response = await ultronTuringApi.generateReport([meterKey], {
              endTime: endTimeStr,
              duration: duration,
              granularity: 'hour',
              format: 'summary',
              utcOffset: '+8:00',
            });
            
            console.log('[EnergyOpt] Report response for', meterKey, 'keys:', Object.keys(response?.data || {}));
            
            if (response?.success && response?.data) {
              const data = response.data as any;
              const endpointKeys = [meter.deviceSN, meterKey, meter.baseSN, meter.id].filter(Boolean) as string[];
              console.log('[EnergyOpt] endpointKeys:', endpointKeys);
              endpointKeys.forEach(key => {
                if (data[key]) {
                  console.log('[EnergyOpt] data[key] keys:', key, Object.keys(data[key] || {}));
                }
                if (data.devices?.[key]) {
                  console.log('[EnergyOpt] data.devices[key] keys:', key, Object.keys(data.devices[key] || {}));
                }
              });
              
              // Format 1: chartData (summary format)
              if (data.chartData && Array.isArray(data.chartData)) {
                console.log('[EnergyOpt] Found chartData with', data.chartData.length, 'points', 'sample:', data.chartData[0]);
                data.chartData.forEach((point: any) => {
                  const pointTime = new Date(point.time || point.timestamp);
                  const hour = pointTime.getHours();
                  let power = 0;
                  if (point.endpoints) {
                    const key = endpointKeys.find(k => point.endpoints[k] !== undefined);
                    if (key) {
                      power = point.endpoints[key];
                    }
                  } else {
                    power = point.avgPower ?? point.power ?? point.watt ?? 0;
                  }
                  console.log(`[EnergyOpt] chartData Hour ${hour}: power=${power}W`);
                  hourlyData[hour] = (hourlyData[hour] ?? 0) + power;
                });
              }
              // Format 1b: data[deviceSN].chartData (summary with device key)
              else if (endpointKeys.some(k => data[k]?.chartData && Array.isArray(data[k].chartData))) {
                const key = endpointKeys.find(k => data[k]?.chartData && Array.isArray(data[k].chartData)) as string;
                console.log('[EnergyOpt] Found data[key].chartData with', data[key].chartData.length, 'points');
                data[key].chartData.forEach((point: any) => {
                  const pointTime = new Date(point.time || point.timestamp);
                  const hour = pointTime.getHours();
                  const power = point.value ?? point.avgPower ?? point.power ?? point.watt ?? point.real_power ?? 0;
                  hourlyData[hour] = (hourlyData[hour] ?? 0) + power;
                });
              }
              // Format 2: detailed format - data[deviceSN].watt array
              else if (endpointKeys.some(k => data[k]?.watt && Array.isArray(data[k].watt))) {
                const key = endpointKeys.find(k => data[k]?.watt && Array.isArray(data[k].watt)) as string;
                console.log('[EnergyOpt] Found detailed watt array with', data[key].watt.length, 'points');
                data[key].watt.forEach((point: any) => {
                  // timestamp is in seconds (Unix timestamp)
                  const pointTime = new Date(point.timestamp * 1000);
                  const hour = pointTime.getHours();
                  const power = point.value ?? 0;
                  console.log(`[EnergyOpt] detailed Hour ${hour}: power=${power}W`);
                  hourlyData[hour] = (hourlyData[hour] ?? 0) + power;
                });
              }
              // Format 3: data[deviceSN].dataPoints
              else if (endpointKeys.some(k => data[k]?.dataPoints && Array.isArray(data[k].dataPoints))) {
                const key = endpointKeys.find(k => data[k]?.dataPoints && Array.isArray(data[k].dataPoints)) as string;
                console.log('[EnergyOpt] Found dataPoints array with', data[key].dataPoints.length, 'points');
                data[key].dataPoints.forEach((point: any) => {
                  const rawTime = point.timestamp ?? point.time;
                  const pointTime = rawTime ? new Date(rawTime < 1e12 ? rawTime * 1000 : rawTime) : null;
                  if (!pointTime) return;
                  const hour = pointTime.getHours();
                  const power = point.value ?? point.watt ?? point.power ?? point.avgPower ?? point.real_power ?? 0;
                  hourlyData[hour] = (hourlyData[hour] ?? 0) + power;
                });
              }
              // Format 4: data.devices[deviceSN].dataPoints / usage / watt
              else if (endpointKeys.some(k => data.devices?.[k]?.dataPoints || data.devices?.[k]?.usage || data.devices?.[k]?.watt)) {
                const key = endpointKeys.find(k => data.devices?.[k]?.dataPoints || data.devices?.[k]?.usage || data.devices?.[k]?.watt) as string;
                const entry = data.devices[key];
                const points = entry.dataPoints || entry.usage || entry.watt || [];
                console.log('[EnergyOpt] Found devices data points:', points.length);
                points.forEach((point: any) => {
                  const rawTime = point.timestamp ?? point.time;
                  const pointTime = rawTime ? new Date(rawTime < 1e12 ? rawTime * 1000 : rawTime) : null;
                  if (!pointTime) return;
                  const hour = pointTime.getHours();
                  const power = point.value ?? point.watt ?? point.power ?? point.avgPower ?? point.real_power ?? 0;
                  hourlyData[hour] = (hourlyData[hour] ?? 0) + power;
                });
              }
              else {
                console.log('[EnergyOpt] Unknown response format, keys:', Object.keys(data));
              }
            }
          } catch (err) {
            console.warn('[EnergyOpt] Failed to fetch data for meter:', meterKey, err);
          }
        }
        
        console.log('[EnergyOpt] Aggregated hourly data:', hourlyData);
        setHistoricalPowerData(hourlyData);
        
      } catch (error) {
        console.error('[EnergyOpt] Failed to fetch historical data:', error);
        setHistoricalPowerData([]);
      } finally {
        setIsLoadingHistoricalData(false);
      }
    };
    
    fetchHistoricalData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchHistoricalData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedMeterRecords, currentTimeStr]);
  
  // Include currentTimeStr as dependency so chart updates when hour changes
  const energyData = useMemo(() => generateEnergyData(
    energyOptimizationConfig?.baselineSchedule || DEFAULT_BASELINE_SCHEDULE,
    selectedMetersPower,
    historicalPowerData.length > 0 ? historicalPowerData : undefined
  ), [energyOptimizationConfig?.baselineSchedule, selectedMetersPower, currentTimeStr, historicalPowerData]);
  
  // Find the latest hour with actual data for the "Latest" indicator
  // Only consider hours up to current hour (don't show future/stale data)
  const latestDataHour = useMemo(() => {
    for (let i = energyData.length - 1; i >= 0; i--) {
      if (energyData[i]?.hasData) {
        return energyData[i].time;
      }
    }
    return null;
  }, [energyData]);

  const latestSavingLabel = useMemo(() => {
    for (let i = energyData.length - 1; i >= 0; i--) {
      const point = energyData[i];
      if (point?.hasData && point.savingPct !== null && point.savingPct !== undefined) {
        const pct = Math.abs(point.savingPct);
        const sign = point.savingPct >= 0 ? '-' : '+';
        return `節能 ${sign}${pct}%`;
      }
    }
    return null;
  }, [energyData]);

  const latestDataIndex = useMemo(() => {
    if (!latestDataHour) return -1;
    return energyData.findIndex(p => p.time === latestDataHour);
  }, [energyData, latestDataHour]);
  
  // Memoize chart components to prevent re-renders from master brightness/colorTemp changes
  const circadianChart = useMemo(() => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={circadianData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <XAxis 
          dataKey="time" 
          tick={{ fontSize: 10, fill: '#6b7280' }}
          axisLine={{ stroke: '#374151' }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis 
          yAxisId="left"
          domain={[0, 7000]}
          ticks={[0, 2000, 4000, 6000, 7000]}
          tick={{ fontSize: 10, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : String(value)}
          allowDataOverflow={false}
        />
        <YAxis 
          yAxisId="right"
          orientation="right"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={{ fontSize: 10, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
          allowDataOverflow={false}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#1e293b', 
            border: '1px solid #374151', 
            borderRadius: '8px',
            fontSize: '14px'
          }}
          labelStyle={{ color: '#9ca3af', fontSize: '14px', fontWeight: 600 }}
          isAnimationActive={false}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="colorTemp"
          stroke="#06b6d4"
          strokeWidth={2}
          dot={false}
          name="Color Temp (K)"
          isAnimationActive={false}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="brightness"
          stroke="#22c55e"
          strokeWidth={2}
          dot={false}
          name="Brightness (%)"
          isAnimationActive={false}
        />
        {/* Current time indicator */}
        <ReferenceLine
          x={currentTimeStr}
          stroke="#a855f7"
          strokeWidth={2}
          strokeDasharray="4 4"
          label={{
            value: 'Now',
            position: 'top',
            fill: '#a855f7',
            fontSize: 10,
          }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  ), [circadianData, currentTimeStr]);
  
  const energyChart = useMemo(() => {
    // Calculate max value for Y axis (only from baseline since optimized can be null)
    const maxPower = Math.max(
      ...energyData.map(d => Math.max(d.baseline, d.optimized || 0))
    );
    const yMax = Math.ceil(maxPower / 1000) * 1000 || 1000; // Round up to nearest 1000W
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={energyData} margin={{ top: 48, right: 10, bottom: 5, left: 5 }}>
          <XAxis 
            dataKey="time" 
            tick={{ fontSize: 10, fill: '#6b7280' }}
            axisLine={{ stroke: '#374151' }}
            tickLine={false}
            interval={1}
            ticks={['00:00','02:00','04:00','06:00','08:00','10:00','12:00','14:00','16:00','18:00','20:00','22:00']}
            allowDuplicatedCategory={false}
          />
          <YAxis 
            domain={[0, yMax]}
            tick={{ fontSize: 10, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${value}`}
            allowDataOverflow={false}
          />
          <Tooltip 
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const dataPoint = payload[0]?.payload;
              if (!dataPoint) return null;
              
              const baseline = dataPoint.baseline;
              const optimized = dataPoint.optimized;
              const isFuture = dataPoint.isFuture;
              const hasData = dataPoint.hasData;
              
              const formatPower = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(2)} kW` : `${v} W`;
              
              return (
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-3">
                  <p className="text-slate-300 font-semibold mb-2">{label}</p>
                  <p className="text-red-400">Baseline : {formatPower(baseline)}</p>
                  {isFuture ? (
                    <p className="text-slate-500 italic">（未來時段）</p>
                  ) : hasData && optimized !== null ? (
                    <>
                      <p className="text-green-400">Optimized : {formatPower(optimized)}</p>
                      {baseline > 0 && (
                        <p className="text-amber-400">
                          節能 : {Math.round(((baseline - optimized) / baseline) * 100) >= 0 ? '-' : '+'}
                          {Math.abs(Math.round(((baseline - optimized) / baseline) * 100))}%
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-500 italic">（無數據）</p>
                  )}
                </div>
              );
            }}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="baseline"
            stroke="#ef4444"
            fill="#ef4444"
            fillOpacity={0.3}
            strokeWidth={2}
            name="Baseline"
            isAnimationActive={false}
            connectNulls={false}
          />
          <Area
            type="monotone"
            dataKey="optimized"
            stroke="#22c55e"
            fill="#22c55e"
            fillOpacity={0.5}
            strokeWidth={2}
            name="Optimized"
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* Latest data indicator - only show if we have data */}
          {latestDataHour && (
            <ReferenceLine
              x={latestDataHour}
              stroke="#a855f7"
              strokeWidth={2}
              strokeDasharray="4 4"
              label={{
                position: 'insideTop',
                content: (props: any) => {
                  const { x, viewBox } = props || {};
                  const text = latestSavingLabel
                    ? `${latestDataHour} · ${latestSavingLabel}`
                    : latestDataHour;
                  if (!text || !viewBox) return null;
                  const paddingX = 10;
                  const paddingY = 6;
                  const fontSize = 14;
                  const textWidth = text.length * 7.5;
                  const boxWidth = textWidth + paddingX * 2;
                  const boxHeight = fontSize + paddingY * 2;
                  const fallbackX = latestDataIndex >= 0 && energyData.length > 1
                    ? viewBox.x + (viewBox.width * (latestDataIndex / (energyData.length - 1)))
                    : viewBox.x + viewBox.width / 2;
                  const xPos = x ?? fallbackX;
                  const boxX = xPos - boxWidth / 2;
                  const minY = viewBox.y + 8;
                  const maxY = viewBox.y + viewBox.height - boxHeight - 8;
                  const desiredY = viewBox.y + viewBox.height * 0.15;
                  const boxY = Math.max(minY, Math.min(desiredY, maxY));
                  const arrowSize = 6;
                  const arrowX = xPos;
                  const arrowY = boxY + boxHeight + arrowSize;
                  return (
                    <g>
                      <rect
                        x={boxX}
                        y={boxY}
                        width={boxWidth}
                        height={boxHeight}
                        rx={6}
                        ry={6}
                        fill="#0f172a"
                        stroke="#22c55e"
                        strokeWidth={1}
                      />
                      <text
                        x={xPos}
                        y={boxY + boxHeight / 2 + fontSize / 3}
                        textAnchor="middle"
                        fill="#22c55e"
                        fontSize={fontSize}
                        fontWeight={700}
                      >
                        {text}
                      </text>
                      <path
                        d={`M ${arrowX - arrowSize} ${arrowY - arrowSize} L ${arrowX + arrowSize} ${arrowY - arrowSize} L ${arrowX} ${arrowY}`}
                        fill="#22c55e"
                      />
                    </g>
                  );
                },
              }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [energyData, latestDataHour]);
  
  // Filter lighting devices - only include actual light devices
  const lightingDevices = useMemo(() => 
    devices.filter(d => 
      d.deviceType === 'light' || 
      d.deviceType === 'dimmer' ||
      d.deviceType?.toLowerCase().includes('light')
    ), [devices]);
  
  // Filter lights for search
  const filteredLights = useMemo(() => {
    if (!lightSearch) return lightingDevices;
    const search = lightSearch.toLowerCase();
    return lightingDevices.filter(d => {
      const sn = getDeviceSN(d)?.toLowerCase() || '';
      const nickname = (d.nickname || '').toLowerCase();
      const deviceName = (d.deviceName || d.displayName || d.name || '').toLowerCase();
      return sn.includes(search) || nickname.includes(search) || deviceName.includes(search);
    });
  }, [lightingDevices, lightSearch]);
  
  // Get lighting device IDs for MCP commands (use selected lights or all lights)
  const lightingDeviceIds = useMemo(() => {
    if (selectedLights.length > 0) {
      return selectedLights;
    }
    return lightingDevices.map(d => d.deviceSN || d.sn || d.deviceId || d.baseSN).filter(Boolean) as string[];
  }, [lightingDevices, selectedLights]);
  
  // Toggle light selection
  const handleToggleLightSelection = (sn: string) => {
    setSelectedLights(prev => {
      const newSelection = prev.includes(sn) 
        ? prev.filter(s => s !== sn) 
        : [...prev, sn];
      try {
        localStorage.setItem('lighting-selected-lights', JSON.stringify(newSelection));
      } catch {}
      return newSelection;
    });
  };
  
  // Select all lights
  const handleSelectAllLights = () => {
    const allSNs = lightingDevices.map(d => getDeviceSN(d)).filter(Boolean) as string[];
    setSelectedLights(allSNs);
    try {
      localStorage.setItem('lighting-selected-lights', JSON.stringify(allSNs));
    } catch {}
  };
  
  // Clear light selection (control all)
  const handleClearLightSelection = () => {
    setSelectedLights([]);
    try {
      localStorage.removeItem('lighting-selected-lights');
    } catch {}
  };

  // Calculate zone stats with demo data for zones without devices
  const zoneStats = useMemo(() => {
    if (zones.length === 0) {
      // Use demo zones with current state
      return initialDemoZones.map(z => ({
        ...z,
        isOn: zoneStates[z.id] ?? z.isOn,
        deviceCount: 5, // Demo zones have simulated devices
      }));
    }
    
    return zones.map((zone) => {
      const zoneDevices = lightingDevices.filter(d => 
        zone.devices.includes(d.deviceId) || zone.devices.includes(d.baseSN)
      );
      const onCount = zoneDevices.filter(d => d.isOn).length;
      // Calculate average brightness from actual device data (use ?? to handle 0 correctly)
      const avgBrightness = zoneDevices.length > 0
        ? Math.round(zoneDevices.reduce((sum, d) => sum + (d.brightness ?? 0), 0) / zoneDevices.length)
        : 0; // No devices = 0%
      // Calculate average color temperature from actual device data
      const avgColorTemp = zoneDevices.length > 0
        ? Math.round(zoneDevices.reduce((sum, d) => sum + (d.colorTemp ?? 4000), 0) / zoneDevices.length)
        : 4000; // Default to neutral white
      
      return {
        id: zone.id,
        name: zone.name,
        brightness: avgBrightness,
        colorTemp: avgColorTemp,
        isOn: zoneDevices.length > 0 ? (zoneStates[zone.id] ?? onCount > 0) : false, // No devices = OFF
        autoEnabled: zoneAutoEnabled[zone.id] ?? false,
        deviceCount: zoneDevices.length, // Track device count
      };
    });
  }, [zones, lightingDevices, zoneStates, zoneAutoEnabled]);
  
  // Get light device IDs for a specific zone
  const getZoneLightIds = useCallback((zoneId: string) => {
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return [];
    
    return lightingDevices
      .filter(d => zone.devices.includes(d.deviceId) || zone.devices.includes(d.baseSN))
      .map(d => getDeviceSN(d))
      .filter(Boolean) as string[];
  }, [zones, lightingDevices]);
  
  // Toggle zone ON/OFF with MCP command
  const handleToggleZone = useCallback(async (zoneId: string) => {
    // Get actual current state from zone stats
    const zone = zoneStats.find(z => z.id === zoneId);
    const currentState = zone?.isOn ?? false;
    const newState = !currentState;
    
    // Optimistic update and save to localStorage
    setZoneStates(prev => {
      const updated = { ...prev, [zoneId]: newState };
      try {
        localStorage.setItem('lighting-zone-states', JSON.stringify(updated));
      } catch {}
      return updated;
    });
    
    const zoneLightIds = getZoneLightIds(zoneId);
    if (zoneLightIds.length === 0) return;
    
    try {
      await ultronTuringApi.executeCommand(newState ? 'on' : 'off', zoneLightIds, {});
    } catch (error) {
      console.error('Failed to toggle zone lights:', error);
      // Rollback on error and save to localStorage
      setZoneStates(prev => {
        const updated = { ...prev, [zoneId]: currentState };
        try {
          localStorage.setItem('lighting-zone-states', JSON.stringify(updated));
        } catch {}
        return updated;
      });
    }
  }, [zoneStats, zoneStates, getZoneLightIds]);
  
  // Set zone brightness with MCP command
  const handleZoneBrightnessChange = useCallback(async (zoneId: string, brightness: number) => {
    // Update local state immediately
    setZoneBrightness(prev => ({ ...prev, [zoneId]: brightness }));
    
    const zoneLightIds = getZoneLightIds(zoneId);
    if (zoneLightIds.length === 0) return;
    
    if (commandDebounce.current) clearTimeout(commandDebounce.current);
    
    commandDebounce.current = setTimeout(async () => {
      try {
        await ultronTuringApi.executeCommand('setBrightness', zoneLightIds, { brightness });
      } catch (error) {
        console.error('Failed to set zone brightness:', error);
      }
    }, 300);
  }, [getZoneLightIds]);
  
  // Set zone color temperature with MCP command
  const handleZoneColorTempChange = useCallback(async (zoneId: string, colorTemp: number) => {
    // Update local state immediately
    setZoneColorTemp(prev => ({ ...prev, [zoneId]: colorTemp }));
    
    const zoneLightIds = getZoneLightIds(zoneId);
    if (zoneLightIds.length === 0) return;
    
    if (commandDebounce.current) clearTimeout(commandDebounce.current);
    
    commandDebounce.current = setTimeout(async () => {
      try {
        await ultronTuringApi.executeCommand('setColorTemperature', zoneLightIds, { temperature: colorTemp });
      } catch (error) {
        console.error('Failed to set zone color temperature:', error);
      }
    }, 300);
  }, [getZoneLightIds]);
  
  // Toggle zone auto mode
  const handleToggleZoneAuto = useCallback((zoneId: string) => {
    setZoneAutoEnabled(prev => {
      const newState = !prev[zoneId];
      const updated = { ...prev, [zoneId]: newState };
      
      // Save to localStorage
      try {
        localStorage.setItem('lighting-zone-auto-enabled', JSON.stringify(updated));
      } catch {}
      
      // If enabling auto, immediately apply circadian values
      if (newState) {
        const now = new Date();
        const { brightness, colorTemp } = getCircadianValues(now.getHours(), now.getMinutes(), circadianSchedule);
        const zoneLightIds = getZoneLightIds(zoneId);
        
        if (zoneLightIds.length > 0) {
          setZoneBrightness(p => ({ ...p, [zoneId]: brightness }));
          setZoneColorTemp(p => ({ ...p, [zoneId]: colorTemp }));
          
          // Send commands
          ultronTuringApi.executeCommand('setBrightness', zoneLightIds, { brightness }).catch(console.error);
          ultronTuringApi.executeCommand('setColorTemperature', zoneLightIds, { temperature: colorTemp }).catch(console.error);
        }
      }
      
      return updated;
    });
  }, [getZoneLightIds, circadianSchedule]);
  
  // Handle Master Brightness change with MCP command
  const handleMasterBrightnessChange = useCallback(async (brightness: number) => {
    setMasterBrightness(brightness);
    
    if (lightingDeviceIds.length === 0) return;
    
    // Debounce to avoid too many API calls
    if (commandDebounce.current) clearTimeout(commandDebounce.current);
    
    commandDebounce.current = setTimeout(async () => {
      setIsCommandLoading(true);
      try {
        await ultronTuringApi.executeCommand('setBrightness', lightingDeviceIds, { brightness });
      } catch (error) {
        console.error('Failed to set brightness:', error);
      } finally {
        setIsCommandLoading(false);
      }
    }, 300);
  }, [lightingDeviceIds]);
  
  // Handle Master Color Temperature change with MCP command
  const handleMasterColorTempChange = useCallback(async (colorTemp: number) => {
    setMasterColorTemp(colorTemp);
    
    if (lightingDeviceIds.length === 0) return;
    
    if (commandDebounce.current) clearTimeout(commandDebounce.current);
    
    commandDebounce.current = setTimeout(async () => {
      setIsCommandLoading(true);
      try {
        await ultronTuringApi.executeCommand('setColorTemperature', lightingDeviceIds, { 
          temperature: colorTemp
        });
      } catch (error) {
        console.error('Failed to set color temperature:', error);
      } finally {
        setIsCommandLoading(false);
      }
    }, 300);
  }, [lightingDeviceIds]);
  
  // Sync to current circadian schedule values
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSyncToCircadian = useCallback(async () => {
    if (lightingDeviceIds.length === 0) return;
    
    const now = new Date();
    const { brightness, colorTemp } = getCircadianValues(now.getHours(), now.getMinutes(), circadianSchedule);
    
    setIsSyncing(true);
    setMasterBrightness(brightness);
    setMasterColorTemp(colorTemp);
    
    try {
      // Send both commands
      await Promise.all([
        ultronTuringApi.executeCommand('setBrightness', lightingDeviceIds, { brightness }),
        ultronTuringApi.executeCommand('setColorTemperature', lightingDeviceIds, { temperature: colorTemp }),
      ]);
    } catch (error) {
      console.error('Failed to sync to circadian:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [lightingDeviceIds]);
  
  // Circadian auto-apply timer (every 15 minutes) - Global controls all zones
  useEffect(() => {
    if (!circadianEnabled) {
      // Global Circadian is OFF, no automatic control
      return;
    }
    
    const applyCircadianValues = () => {
      const now = new Date();
      const { brightness, colorTemp } = getCircadianValues(now.getHours(), now.getMinutes(), circadianSchedule);
      
      // Update master values
      setMasterBrightness(brightness);
      setMasterColorTemp(colorTemp);
      
      // Apply to all zones with lights (Global ON = all zones follow circadian)
      zones.forEach((zone) => {
        const zoneLightIds = getZoneLightIds(zone.id);
        if (zoneLightIds.length > 0) {
          setZoneBrightness(prev => ({ ...prev, [zone.id]: brightness }));
          setZoneColorTemp(prev => ({ ...prev, [zone.id]: colorTemp }));
          ultronTuringApi.executeCommand('setBrightness', zoneLightIds, { brightness }).catch(console.error);
          ultronTuringApi.executeCommand('setColorTemperature', zoneLightIds, { temperature: colorTemp }).catch(console.error);
        }
      });
    };
    
    // Run immediately when Global Circadian is enabled
    applyCircadianValues();
    
    // Then run every 15 minutes
    const interval = setInterval(applyCircadianValues, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [circadianEnabled, zones, getZoneLightIds]);
  
  // Zone-level auto mode (only active when Global Circadian is OFF)
  useEffect(() => {
    // Skip if Global Circadian is ON (Global handles all zones)
    if (circadianEnabled) {
      return;
    }
    
    // Check if any zones have auto enabled
    const autoEnabledZoneIds = Object.entries(zoneAutoEnabled)
      .filter(([_, isEnabled]) => isEnabled)
      .map(([zoneId]) => zoneId);
    
    if (autoEnabledZoneIds.length === 0) {
      return;
    }
    
    const applyZoneCircadianValues = () => {
      const now = new Date();
      const { brightness, colorTemp } = getCircadianValues(now.getHours(), now.getMinutes(), circadianSchedule);
      
      autoEnabledZoneIds.forEach((zoneId) => {
        const zoneLightIds = getZoneLightIds(zoneId);
        if (zoneLightIds.length > 0) {
          setZoneBrightness(prev => ({ ...prev, [zoneId]: brightness }));
          setZoneColorTemp(prev => ({ ...prev, [zoneId]: colorTemp }));
          ultronTuringApi.executeCommand('setBrightness', zoneLightIds, { brightness }).catch(console.error);
          ultronTuringApi.executeCommand('setColorTemperature', zoneLightIds, { temperature: colorTemp }).catch(console.error);
        }
      });
    };
    
    // Run every 15 minutes (don't run immediately - handleToggleZoneAuto already does that)
    const interval = setInterval(applyZoneCircadianValues, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [circadianEnabled, zoneAutoEnabled, getZoneLightIds]);
  
  // Initialize master values from average of actual device states
  useEffect(() => {
    if (lightingDevices.length > 0) {
      const devicesWithBrightness = lightingDevices.filter(d => d.brightness !== undefined);
      const devicesWithColorTemp = lightingDevices.filter(d => d.colorTemp !== undefined);
      
      if (devicesWithBrightness.length > 0) {
        const avgBrightness = Math.round(
          devicesWithBrightness.reduce((sum, d) => sum + (d.brightness || 50), 0) / devicesWithBrightness.length
        );
        setMasterBrightness(avgBrightness);
      }
      
      if (devicesWithColorTemp.length > 0) {
        const avgColorTemp = Math.round(
          devicesWithColorTemp.reduce((sum, d) => sum + (d.colorTemp || 4500), 0) / devicesWithColorTemp.length
        );
        setMasterColorTemp(avgColorTemp);
      }
    }
  }, [lightingDevices]);
  
  // Calculate summary stats
  const activeCount = zoneStats.filter(z => z.isOn).length;
  const offCount = zoneStats.length - activeCount;
  
  // Calculate energy savings (demo)
  const energySavings = useMemo(() => {
    return circadianEnabled ? 23 : 0;
  }, [circadianEnabled]);

  return (
    <div className="space-y-4">
      {/* Top Control Cards - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Master Brightness */}
        <div className={`bg-[#0C2340] rounded-xl p-5 ${lightingDevices.length === 0 ? 'opacity-50' : ''}`}>
          <div 
            className={`flex items-center justify-between mb-4 ${lightingDevices.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'} transition-opacity`}
            onClick={() => lightingDevices.length > 0 && setShowLightSelector(true)}
          >
            <div className="flex items-center gap-2">
              <Lightbulb className={`w-4 h-4 ${lightingDevices.length === 0 ? 'text-slate-500' : 'text-amber-400'}`} />
              <span className="text-base text-slate-400">Master Brightness</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${lightingDevices.length === 0 ? 'text-slate-500 bg-slate-700/50' : 'text-cyan-400 bg-cyan-900/30'}`}>
                {lightingDevices.length === 0 ? '無燈具' : selectedLights.length > 0 ? `${selectedLights.length} 燈具` : `全部 (${lightingDevices.length})`}
              </span>
            </div>
            <span className={`text-3xl font-bold ${lightingDevices.length === 0 ? 'text-slate-500' : 'text-white'}`}>{masterBrightness}%</span>
          </div>
          <div className={`relative w-full h-3 ${isCommandLoading || lightingDevices.length === 0 ? 'opacity-50' : ''}`}>
            <div className="absolute inset-0 rounded-full bg-slate-700" />
            <div 
              className={`absolute left-0 top-0 h-full rounded-full ${lightingDevices.length === 0 ? 'bg-slate-600' : 'bg-gradient-to-r from-cyan-600 to-cyan-400'}`}
              style={{ width: `${masterBrightness}%` }}
            />
            <input
              type="range"
              min="0"
              max="100"
              value={masterBrightness}
              onChange={(e) => handleMasterBrightnessChange(parseInt(e.target.value))}
              disabled={isCommandLoading || lightingDevices.length === 0}
              className={`absolute inset-0 w-full h-full opacity-0 ${isCommandLoading || lightingDevices.length === 0 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ zIndex: 10 }}
            />
            <div 
              className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full shadow-lg pointer-events-none ${lightingDevices.length === 0 ? 'bg-slate-500' : 'bg-white'}`}
              style={{ left: `calc(${masterBrightness}% - 10px)` }}
            />
          </div>
          <div className="flex justify-between text-sm text-slate-500 mt-2">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
        
        {/* Color Temperature */}
        <div className={`bg-[#0C2340] rounded-xl p-5 ${lightingDevices.length === 0 ? 'opacity-50' : ''}`}>
          <div 
            className={`flex items-center justify-between mb-4 ${lightingDevices.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'} transition-opacity`}
            onClick={() => lightingDevices.length > 0 && setShowLightSelector(true)}
          >
            <div className="flex items-center gap-2">
              <Sun className={`w-4 h-4 ${lightingDevices.length === 0 ? 'text-slate-500' : 'text-orange-400'}`} />
              <span className="text-base text-slate-400">Color Temperature</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${lightingDevices.length === 0 ? 'text-slate-500 bg-slate-700/50' : 'text-cyan-400 bg-cyan-900/30'}`}>
                {lightingDevices.length === 0 ? '無燈具' : selectedLights.length > 0 ? `${selectedLights.length} 燈具` : `全部 (${lightingDevices.length})`}
              </span>
            </div>
            <span className={`text-3xl font-bold ${lightingDevices.length === 0 ? 'text-slate-500' : 'text-white'}`}>{masterColorTemp}K</span>
          </div>
          <div className={`relative w-full h-3 ${isCommandLoading || lightingDevices.length === 0 ? 'opacity-50' : ''}`}>
            <div 
              className="absolute inset-0 rounded-full"
              style={{ background: lightingDevices.length === 0 ? '#475569' : 'linear-gradient(to right, #f97316 0%, #fbbf24 30%, #ffffff 50%, #38bdf8 70%, #06b6d4 100%)' }}
            />
            <input
              type="range"
              min="2700"
              max="6500"
              value={masterColorTemp}
              onChange={(e) => handleMasterColorTempChange(parseInt(e.target.value))}
              disabled={isCommandLoading || lightingDevices.length === 0}
              className={`absolute inset-0 w-full h-full opacity-0 ${isCommandLoading || lightingDevices.length === 0 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ zIndex: 10 }}
            />
            <div 
              className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full shadow-lg pointer-events-none border border-slate-300 ${lightingDevices.length === 0 ? 'bg-slate-500' : 'bg-white'}`}
              style={{ left: `calc(${((masterColorTemp - 2700) / (6500 - 2700)) * 100}% - 10px)` }}
            />
          </div>
          <div className="flex justify-between text-xs mt-2">
            <span className={`flex items-center gap-1 ${lightingDevices.length === 0 ? 'text-slate-500' : 'text-orange-400'}`}>
              <span className={`w-2 h-2 rounded-full ${lightingDevices.length === 0 ? 'bg-slate-500' : 'bg-orange-400'}`} />
              Warm
            </span>
            <span className="text-slate-500">Neutral</span>
            <span className={`flex items-center gap-1 ${lightingDevices.length === 0 ? 'text-slate-500' : 'text-cyan-400'}`}>
              Cool
              <span className={`w-2 h-2 rounded-full ${lightingDevices.length === 0 ? 'bg-slate-500' : 'bg-cyan-400'}`} />
            </span>
          </div>
        </div>
        
        {/* Circadian Adaptive */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-400" />
              <span className="text-base text-slate-400">Circadian Adaptive</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={circadianEnabled}
                onChange={() => {
                  const newValue = !circadianEnabled;
                  setCircadianEnabled(newValue);
                  try {
                    localStorage.setItem('lighting-circadian-enabled', String(newValue));
                  } catch {}
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-800/50 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
            </label>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Automatically adjusts lighting based on time of day and natural light levels
          </p>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-sm font-medium ${
              circadianEnabled 
                ? 'bg-cyan-500/20 text-cyan-400' 
                : 'bg-slate-600 text-slate-400'
            }`}>
              {circadianEnabled ? 'Active' : 'Inactive'}
            </span>
            {circadianEnabled && (
              <span className="text-xs text-emerald-400">Saving {energySavings}% energy</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Charts Section - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Circadian Schedule */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-400" />
              <h3 className="text-base font-semibold text-white">Circadian Schedule</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingSchedule([...circadianSchedule]);
                  setShowScheduleEditor(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-slate-700/50 text-slate-300 hover:bg-purple-900/30 hover:text-purple-400"
                title="編輯排程"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleSyncToCircadian}
                disabled={isSyncing || lightingDeviceIds.length === 0}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isSyncing 
                    ? 'bg-cyan-900/30 text-cyan-400 cursor-wait' 
                    : 'bg-slate-700/50 text-slate-300 hover:bg-cyan-900/30 hover:text-cyan-400'
                }`}
                title="立即同步至當前排程值"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>Sync</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-4">Today's automated lighting curve</p>
          
          <div className="h-56">
            {circadianChart}
          </div>
        </div>
        
        {/* Energy Optimization */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <h3 className="text-base font-semibold text-white">Energy Optimization</h3>
            </div>
            <button
              onClick={() => {
                setTempSelectedMeters(energyOptimizationConfig?.selectedMeterIds || []);
                setTempBaselineSchedule(energyOptimizationConfig?.baselineSchedule || [...DEFAULT_BASELINE_SCHEDULE]);
                setShowEnergyOptModal(true);
              }}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
              title="設定電錶和基準功率"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-slate-500 mb-4">Baseline vs optimized consumption</p>
          
          <div className="h-56">
            {energyChart}
          </div>
        </div>
      </div>
      
      {/* Zone Controls Section */}
      <div className="bg-[#0C2340] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Settings className="w-4 h-4 text-slate-400" />
            <div>
              <h3 className="text-base font-semibold text-white">Zone Controls</h3>
              <p className="text-sm text-slate-500">Individual lighting zone management</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-slate-400">{activeCount} Active</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-800/300" />
              <span className="text-slate-400">{offCount} Off</span>
            </span>
            {onOpenZoneSelector && (
              <button
                onClick={onOpenZoneSelector}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
                title="選擇區域"
              >
                <Settings className="w-3 h-3" />
                <span>區域</span>
                {typeof selectedZoneCount === 'number' && (
                  <span className="text-slate-500">({selectedZoneCount})</span>
                )}
              </button>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {zoneStats.map((zone) => (
            <div
              key={zone.id}
              className={`rounded-lg p-4 border transition-all ${
                zone.deviceCount === 0
                  ? 'bg-[#050d18] border-slate-700/20'
                  : zone.isOn 
                    ? 'bg-[#050d18] border-slate-700/50' 
                    : 'bg-[#050d18] border-slate-700/30'
              }`}
            >
              {/* Zone with no devices - show empty state */}
              {zone.deviceCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                    <span className="text-base font-medium text-slate-500">{zone.name}</span>
                  </div>
                  <Lightbulb className="w-8 h-8 text-slate-600 mb-2" />
                  <p className="text-sm text-slate-500">本區塊沒有任何燈具</p>
                </div>
              ) : (
                <>
                  <div className={`flex items-center justify-between ${zone.isOn ? 'mb-3' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${zone.isOn ? 'bg-cyan-400' : 'bg-slate-500'}`} />
                      <span className={`text-base font-medium ${zone.isOn ? 'text-white' : 'text-slate-400'}`}>{zone.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => setViewingZoneId(zone.id)}
                        className="p-1.5 rounded-lg transition-colors text-slate-400 hover:text-slate-200 hover:bg-slate-700"
                        title="View Zone Lights"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleToggleZone(zone.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          zone.isOn 
                            ? 'text-cyan-400 hover:bg-cyan-400/10' 
                            : 'text-slate-500 hover:bg-slate-700'
                        }`}
                        title={zone.isOn ? 'Turn Off' : 'Turn On'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
              
              {/* Only show details when zone is ON */}
              {zone.isOn && (() => {
                const currentBrightness = zoneBrightness[zone.id] ?? zone.brightness;
                const currentColorTemp = zoneColorTemp[zone.id] ?? zone.colorTemp;
                const zoneAutoOn = zoneAutoEnabled[zone.id] ?? false;
                // Disable sliders when Global Circadian is ON OR Zone Auto is ON
                const isDisabled = circadianEnabled || zoneAutoOn;
                const isAutoEnabled = circadianEnabled || zoneAutoOn;
                return (
                  <>
                    {/* Brightness Slider */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Brightness</span>
                        <span className="text-slate-300">{currentBrightness}%</span>
                      </div>
                      <div className={`relative w-full h-2 ${isDisabled ? 'opacity-50' : ''}`}>
                        <div className="absolute inset-0 rounded-full bg-slate-700" />
                        <div 
                          className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-cyan-600 to-cyan-400"
                          style={{ width: `${currentBrightness}%` }}
                        />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={currentBrightness}
                          onChange={(e) => handleZoneBrightnessChange(zone.id, parseInt(e.target.value))}
                          disabled={isDisabled}
                          className={`absolute inset-0 w-full h-full opacity-0 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          style={{ zIndex: 10 }}
                        />
                        <div 
                          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg pointer-events-none"
                          style={{ left: `calc(${currentBrightness}% - 8px)` }}
                        />
                      </div>
                    </div>
                    
                    {/* Color Temperature Slider */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Temperature</span>
                        <span className="text-slate-300">{currentColorTemp}K</span>
                      </div>
                      <div className={`relative w-full h-2 ${isDisabled ? 'opacity-50' : ''}`}>
                        <div 
                          className="absolute inset-0 rounded-full"
                          style={{ background: 'linear-gradient(to right, #f97316 0%, #fbbf24 30%, #ffffff 50%, #38bdf8 70%, #06b6d4 100%)' }}
                        />
                        <input
                          type="range"
                          min="2700"
                          max="6500"
                          value={currentColorTemp}
                          onChange={(e) => handleZoneColorTempChange(zone.id, parseInt(e.target.value))}
                          disabled={isDisabled}
                          className={`absolute inset-0 w-full h-full opacity-0 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          style={{ zIndex: 10 }}
                        />
                        <div 
                          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg pointer-events-none border border-slate-300"
                          style={{ left: `calc(${((currentColorTemp - 2700) / (6500 - 2700)) * 100}% - 8px)` }}
                        />
                      </div>
                    </div>
                    
                    {/* Auto Toggle / Global Following Indicator */}
                    <div className="flex items-center justify-between">
                      {circadianEnabled ? (
                        // When Global Circadian is ON, show "Following Global" label instead of toggle
                        <span className="text-xs text-purple-400 ml-auto">跟隨 Global Circadian</span>
                      ) : (
                        // When Global Circadian is OFF, show individual zone Auto toggle
                        <>
                          {isAutoEnabled && (
                            <span className="text-xs text-purple-400">跟隨 Circadian 排程</span>
                          )}
                          <label className="flex items-center gap-2 cursor-pointer ml-auto">
                            <span className="text-sm text-slate-500">Auto</span>
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={isAutoEnabled}
                                onChange={() => handleToggleZoneAuto(zone.id)}
                                className="sr-only peer"
                              />
                              <div className="w-8 h-4 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-800/50 after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-cyan-600"></div>
                            </div>
                          </label>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
              </>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Power Meter Selector Modal */}
      {showPowerMeterSelector && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowPowerMeterSelector(false)}
        >
          <div 
            className="bg-[#0C2340] border border-slate-700/30 rounded-xl w-full max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
              <h3 className="text-lg font-semibold text-white">選擇電錶</h3>
              <button 
                onClick={() => setShowPowerMeterSelector(false)}
                className="p-1 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search */}
            <div className="p-4 border-b border-slate-700/30">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="搜尋電錶..."
                  value={powerMeterSearch}
                  onChange={(e) => setPowerMeterSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
            
            {/* Options */}
            <div className="max-h-64 overflow-y-auto p-2">
              {/* Total Option */}
              <button
                onClick={() => handleSelectPowerMeter('')}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                  selectedPowerMeter === '' ? 'bg-cyan-900/20' : 'hover:bg-slate-700/50'
                }`}
              >
                <div className="text-left">
                  <p className="text-white font-medium">Total ({powerMeters.length} 電錶)</p>
                  <p className="text-sm text-slate-500">顯示所有電錶總和</p>
                </div>
                {selectedPowerMeter === '' && <Check className="w-5 h-5 text-cyan-400" />}
              </button>
              
              {/* Individual Meters */}
              {filteredPowerMeters.map((pm) => {
                const sn = getDeviceSN(pm);
                const displayName = pm.nickname || pm.deviceName || pm.displayName || pm.name || sn;
                return (
                  <button
                    key={sn}
                    onClick={() => handleSelectPowerMeter(sn || '')}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      selectedPowerMeter === sn ? 'bg-cyan-900/20' : 'hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="text-left">
                      <p className="text-white font-medium">{displayName}</p>
                      {pm.nickname && <p className="text-sm text-slate-500">{sn}</p>}
                    </div>
                    {selectedPowerMeter === sn && <Check className="w-5 h-5 text-cyan-400" />}
                  </button>
                );
              })}
              
              {filteredPowerMeters.length === 0 && powerMeterSearch && (
                <p className="text-center text-slate-500 py-4">找不到符合的電錶</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Light Selector Modal */}
      {showLightSelector && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowLightSelector(false)}
        >
          <div 
            className="bg-[#0C2340] border border-slate-700/30 rounded-xl w-full max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
              <div>
                <h3 className="text-lg font-semibold text-white">選擇燈具群控</h3>
                <p className="text-sm text-slate-500">
                  已選擇 {selectedLights.length > 0 ? selectedLights.length : lightingDevices.length} / {lightingDevices.length} 燈具
                </p>
              </div>
              <button 
                onClick={() => setShowLightSelector(false)}
                className="p-1 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Quick Actions */}
            <div className="p-4 border-b border-slate-700/30 flex gap-2">
              <button
                onClick={handleSelectAllLights}
                className="flex-1 px-3 py-2 bg-cyan-900/30 text-cyan-400 rounded-lg hover:bg-cyan-900/50 transition-colors text-sm font-medium"
              >
                全選
              </button>
              <button
                onClick={handleClearLightSelection}
                className="flex-1 px-3 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700/70 transition-colors text-sm font-medium"
              >
                取消選擇
              </button>
            </div>
            
            {/* Search */}
            <div className="p-4 border-b border-slate-700/30">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="搜尋燈具..."
                  value={lightSearch}
                  onChange={(e) => setLightSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
            
            {/* Light List */}
            <div className="max-h-64 overflow-y-auto p-2">
              {filteredLights.map((light) => {
                const sn = getDeviceSN(light);
                const displayName = light.nickname || light.deviceName || light.displayName || light.name || sn;
                const isSelected = sn ? selectedLights.includes(sn) : false;
                const isOn = light.isOn;
                
                return (
                  <button
                    key={sn}
                    onClick={() => sn && handleToggleLightSelection(sn)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      isSelected ? 'bg-cyan-900/20' : 'hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isOn ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
                        <Lightbulb className={`w-4 h-4 ${isOn ? 'text-amber-400' : 'text-slate-500'}`} />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium">{displayName}</p>
                        <p className="text-xs text-slate-500">{sn}</p>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-cyan-500 border-cyan-500' : 'border-slate-600'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                );
              })}
              
              {filteredLights.length === 0 && lightSearch && (
                <p className="text-center text-slate-500 py-4">找不到符合的燈具</p>
              )}
              
              {lightingDevices.length === 0 && (
                <p className="text-center text-slate-500 py-4">尚未匯入燈具裝置</p>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-700/30">
              <button
                onClick={() => setShowLightSelector(false)}
                className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zone Light Viewer Modal */}
      {viewingZoneId && (
        <ZoneLightViewerModal
          zoneId={viewingZoneId}
          zoneName={zones.find(z => z.id === viewingZoneId)?.name || 'Zone'}
          lights={lightingDevices.filter(d => {
            const zone = zones.find(z => z.id === viewingZoneId);
            return zone?.devices.includes(d.deviceId) || zone?.devices.includes(d.baseSN);
          })}
          onClose={() => setViewingZoneId(null)}
        />
      )}
      
      {/* Schedule Editor Modal */}
      {showScheduleEditor && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowScheduleEditor(false)}
        >
          <div 
            className="bg-[#0C2340] border border-slate-700/30 rounded-xl w-full max-w-lg mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700/30">
              <div>
                <h3 className="text-lg font-semibold text-white">編輯 Circadian 排程</h3>
                <p className="text-sm text-slate-500">調整各時段的亮度與色溫</p>
              </div>
              <button 
                onClick={() => setShowScheduleEditor(false)}
                className="p-1 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Schedule Points */}
            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-4">
              {editingSchedule.map((point, index) => (
                <div key={index} className="bg-[#050d18] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">
                      {point.hour.toString().padStart(2, '0')}:00
                    </span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-cyan-400">{point.colorTemp}K</span>
                      <span className="text-emerald-400">{point.brightness}%</span>
                    </div>
                  </div>
                  
                  {/* Brightness Slider */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">亮度</span>
                      <span className="text-emerald-400">{point.brightness}%</span>
                    </div>
                    <div className="relative w-full h-2">
                      <div className="absolute inset-0 rounded-full bg-slate-700" />
                      <div 
                        className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400"
                        style={{ width: `${point.brightness}%` }}
                      />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={point.brightness}
                        onChange={(e) => {
                          const newSchedule = [...editingSchedule];
                          newSchedule[index] = { ...point, brightness: parseInt(e.target.value) };
                          setEditingSchedule(newSchedule);
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        style={{ zIndex: 10 }}
                      />
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg pointer-events-none"
                        style={{ left: `calc(${point.brightness}% - 8px)` }}
                      />
                    </div>
                  </div>
                  
                  {/* Color Temperature Slider */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">色溫</span>
                      <span className="text-cyan-400">{point.colorTemp}K</span>
                    </div>
                    <div className="relative w-full h-2">
                      <div 
                        className="absolute inset-0 rounded-full"
                        style={{ background: 'linear-gradient(to right, #f97316 0%, #fbbf24 30%, #ffffff 50%, #38bdf8 70%, #06b6d4 100%)' }}
                      />
                      <input
                        type="range"
                        min="2700"
                        max="6500"
                        value={point.colorTemp}
                        onChange={(e) => {
                          const newSchedule = [...editingSchedule];
                          newSchedule[index] = { ...point, colorTemp: parseInt(e.target.value) };
                          setEditingSchedule(newSchedule);
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        style={{ zIndex: 10 }}
                      />
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg pointer-events-none border border-slate-300"
                        style={{ left: `calc(${((point.colorTemp - 2700) / (6500 - 2700)) * 100}% - 8px)` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-700/30 flex gap-3">
              <button
                onClick={() => {
                  setEditingSchedule([...DEFAULT_CIRCADIAN_SCHEDULE]);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700/70 transition-colors font-medium"
                title="還原預設值"
              >
                <RotateCcw className="w-4 h-4" />
                還原預設
              </button>
              <button
                onClick={() => {
                  setCircadianSchedule(editingSchedule);
                  try {
                    localStorage.setItem('lighting-circadian-schedule', JSON.stringify(editingSchedule));
                  } catch {}
                  setShowScheduleEditor(false);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
              >
                <Save className="w-4 h-4" />
                儲存排程
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Energy Optimization Settings Modal - Using Portal - Two Column Layout */}
      {showEnergyOptModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => setShowEnergyOptModal(false)}
        >
          <div 
            className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden mx-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-slate-700/30 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Energy Optimization 設定
              </h3>
              <button 
                onClick={() => setShowEnergyOptModal(false)}
                className="text-slate-400 hover:text-slate-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Two Column Content */}
            <div className="flex-1 flex gap-4 p-4 min-h-0">
              {/* Left Column - Baseline Schedule */}
              <div 
                className="w-1/2 flex flex-col min-h-0"
              >
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <label className="text-sm font-medium text-slate-300">基準功率排程 (W)</label>
                  <button
                    onClick={() => setTempBaselineSchedule([...DEFAULT_BASELINE_SCHEDULE])}
                    className="text-xs text-slate-400 hover:text-slate-300 flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    還原預設
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-3 flex-shrink-0">設定每小時的基準功率，用於計算節能百分比</p>
                <div 
                  className="flex-1 overflow-y-auto pr-2 custom-scrollbar"
                  style={{ maxHeight: '55vh' }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {tempBaselineSchedule.map((power, hour) => (
                      <div key={hour} className="flex items-center gap-2 bg-slate-800/30 rounded-lg p-2">
                        <span className="text-xs text-slate-400 w-10 font-mono">{hour.toString().padStart(2, '0')}:00</span>
                        <input
                          type="number"
                          value={power}
                          onChange={(e) => {
                            const newSchedule = [...tempBaselineSchedule];
                            newSchedule[hour] = parseInt(e.target.value) || 0;
                            setTempBaselineSchedule(newSchedule);
                          }}
                          className="flex-1 px-2 py-1.5 bg-[#050d18] border border-slate-700/50 rounded text-white text-sm text-center focus:outline-none focus:border-cyan-500"
                        />
                        <span className="text-xs text-slate-500 w-6">W</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Divider */}
              <div className="w-px bg-slate-700/50 flex-shrink-0" />
              
              {/* Right Column - Power Meter Selection */}
              <div 
                className="w-1/2 flex flex-col min-h-0"
              >
                <label className="text-sm font-medium text-slate-300 mb-3 flex-shrink-0">
                  選擇電錶 ({availablePowerMeters.length})
                </label>
                <div 
                  ref={scrollableListRef}
                  className="flex-1 overflow-y-auto pr-2 space-y-2 custom-scrollbar"
                  style={{ maxHeight: '55vh' }}
                >
                  {availablePowerMeters.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">目前沒有可用的電錶</p>
                  ) : (
                    availablePowerMeters.map(meter => (
                      <div
                        key={meter.id}
                        onClick={() => {
                          if (tempSelectedMeters.includes(meter.id)) {
                            setTempSelectedMeters(tempSelectedMeters.filter(id => id !== meter.id));
                          } else {
                            setTempSelectedMeters([...tempSelectedMeters, meter.id]);
                          }
                        }}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                          tempSelectedMeters.includes(meter.id)
                            ? 'border-cyan-500 bg-cyan-900/20'
                            : 'border-slate-700/50 hover:bg-slate-700/50'
                        }`}
                      >
                        {tempSelectedMeters.includes(meter.id) ? (
                          <CheckSquare className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{meter.name}</p>
                          <p className="text-xs text-slate-400">
                            {meter.power !== undefined 
                              ? (meter.power >= 1000 ? `${(meter.power / 1000).toFixed(2)} kW` : `${Math.round(meter.power)} W`)
                              : '0 W'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between flex-shrink-0">
              <p className="text-sm text-slate-400">
                {tempSelectedMeters.length > 0 
                  ? (() => {
                      const totalPower = availablePowerMeters
                        .filter(m => tempSelectedMeters.includes(m.id))
                        .reduce((sum, m) => sum + (m.power || 0), 0);
                      const powerStr = totalPower >= 1000 ? `${(totalPower / 1000).toFixed(2)} kW` : `${Math.round(totalPower)} W`;
                      return `${tempSelectedMeters.length}/${availablePowerMeters.length} 已選擇，總功率: ${powerStr}`;
                    })()
                  : '尚未選擇電錶'}
              </p>
              <button
                onClick={() => {
                  const newConfig: EnergyOptimizationConfig = {
                    selectedMeterIds: tempSelectedMeters,
                    baselinePower: tempBaselineSchedule.reduce((a, b) => a + b, 0) / 24, // Average for legacy
                    baselineSchedule: tempBaselineSchedule,
                  };
                  onEnergyOptimizationConfigChange?.(newConfig);
                  setShowEnergyOptModal(false);
                }}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                儲存
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
    </div>
  );
};

export default AdaptiveLightingView;
